import { ExternalLink, User } from 'lucide-react';
import { useEffect, useState } from "react";
import { BiSolidEdit } from "react-icons/bi";
import { FaWhatsapp } from "react-icons/fa";
import {
  FiCalendar,
  FiCheckCircle,
  FiEdit2,
  FiLock,
  FiMapPin,
  FiRotateCcw,
  FiTrash2,
  FiUnlock
} from "react-icons/fi";
import { IoIosArrowBack } from "react-icons/io";
import { useNavigate, useParams } from "react-router-dom";
import { user as loggedAdmin } from "../../classes/user.js";
import BoxAlert from "../../components/boxAlert/index.jsx";
import Header from "../../components/header/index.jsx";
import { Loading } from "../../components/loading";
import { formatInputCurrencyValues } from "../../scripts/formatInputCurrencyValues";
import formatValues from "../../scripts/formatValues";
import { getCompanies } from "../../scripts/getCompanies";
import { getCompanyData } from "../../scripts/getCompanyData";
import normalizeValues from "../../scripts/normalizeValues";
import axios from "../../services/axios-config";
import ConfirmAddressData from "../clients/components/confirmAddressData/index.jsx";
import EditData from "../clients/components/editData/index.jsx";
import UserBindingsModal from "../users/components/userBindingsModal/index.jsx";
import ClientCollaboratorsModal from "./components/clientCollaboratorsModal/index.jsx";
import ClientCustomersModal from "./components/clientCustomersModal/index.jsx";
import ClientLoginsModal from "./components/clientLoginsModal/index.jsx";
import ClientNoteModal from "./components/clientNoteModal/index.jsx";
import styles from "./css/clientDetails.module.css";
import { deleteCompany } from "./services/deleteCompany.js";
import { restoreCompany } from "./services/restoreCompany.js";
import { updateNextPayment } from "./services/updateNextPayment.js";

// Status de cobranca calculado no backend (getCompaniesService), cruzando o trial, o controle de
// pagamento e o historico real de pagamentos confirmados (companies.payments_register). Nao usar
// payment_control.status isoladamente: e apenas uma flag de acesso (bloqueio de login), setada
// TRUE desde a criacao da empresa mesmo antes de qualquer cobranca real ter acontecido.
const BILLING_STATUS_META = {
  TRIALING: { color: "var(--color-secondary-background)", text: "Em teste (sem cobranca)" },
  NO_PAYMENT: { color: "grey", text: "Sem pagamentos registrados" },
  OVERDUE: { color: "var(--color-red)", text: "Inadimplente" },
  ACTIVE: { color: "var(--color-green)", text: "Em dia" },
};

const EMPTY_METRICS = {
  productsCount: 0,
  lastProductAt: null,
  customersCount: 0,
  lastCustomerAt: null,
  loginsPerDay: 0,
  lastLoginAt: null,
  collaboratorsCount: 0,
};

export default function ClientDetails() {
  const navigate = useNavigate();
  const { id } = useParams();

  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingInformations, setLoadingInformations] = useState(false);
  const [screenError, setScreenError] = useState(null);

  const [warnAlert, setWarnAlert] = useState({ show: false, title: null, text: null });
  const [accountOperatingAlert, setAccountOperatingAlert] = useState(false);
  const [confirmPaymentAlert, setConfirmPaymentAlert] = useState(false);
  const [confirmPaymentDateAlert, setConfirmPaymentDateAlert] = useState(false);
  const [confirmPaymentDateValue, setConfirmPaymentDateValue] = useState(null);
  const [nextPaymentDateValue, setNextPaymentDateValue] = useState(null);
  const [confirmAddressDataAlert, setConfirmAddressDataAlert] = useState(false);
  const [extendTrialAlert, setExtendTrialAlert] = useState(false);
  const [extendTrialDateValue, setExtendTrialDateValue] = useState(null);
  const [editNextPaymentAlert, setEditNextPaymentAlert] = useState(false);
  const [editNextPaymentCost, setEditNextPaymentCost] = useState("");
  const [editNextPaymentDateValue, setEditNextPaymentDateValue] = useState(null);
  const [showEditCompanyDataComponent, setShowEditCompanyDataComponent] = useState(false);
  const [notesModalOpen, setNotesModalOpen] = useState(false);
  const [customersModalOpen, setCustomersModalOpen] = useState(false);
  const [loginsModalOpen, setLoginsModalOpen] = useState(false);
  const [collaboratorsModalOpen, setCollaboratorsModalOpen] = useState(false);
  const [userBindingsModalOpen, setUserBindingsModalOpen] = useState(false);
  const [metrics, setMetrics] = useState(EMPTY_METRICS);
  const [companyToDelete, setCompanyToDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [restoring, setRestoring] = useState(false);

  useEffect(() => {
    handleGetCompanyData();
  }, [id]);

  const handleGetCompanyData = async () => {
    try {
      setLoading(true);
      setScreenError(null);

      const [companies, detailResponse] = await Promise.all([
        getCompanies(),
        getCompanyData(id),
      ]);

      const currentCompany = companies.find((item) => String(item.id) === String(id));

      if (!currentCompany) {
        setScreenError("Nao foi possivel encontrar a empresa solicitada.");
        return;
      }

      const detailData = detailResponse?.data || {};

      const responseMetrics = detailData.metrics || {};
      setMetrics({
        productsCount: responseMetrics.productsCount ?? 0,
        lastProductAt: responseMetrics.lastProductAt ?? null,
        customersCount: responseMetrics.customersCount ?? 0,
        lastCustomerAt: responseMetrics.lastCustomerAt ?? null,
        loginsPerDay: responseMetrics.loginsPerDay ?? 0,
        lastLoginAt: responseMetrics.lastLoginAt ?? null,
        collaboratorsCount: responseMetrics.collaboratorsCount ?? 0,
      });

      const responseNotes = detailData.notes ?? "";

      setCompany({
        ...currentCompany,
        ownerData: detailData.ownerData || {},
        address: detailData.address || {},
        privileges: detailData.companyPrivileges || {},
        fiscal: detailData.fiscal || {},
        paymentsRegister: detailData.paymentsRegister || [],
        notes: responseNotes,
      });

    } catch (error) {
      console.error("ERROR - GET CLIENT DETAILS FUNCTION:", error);
      setScreenError("Nao foi possivel carregar os dados da empresa no momento.");

    } finally {
      setLoading(false);
    }
  };

  // FORMATA UMA DATA (SEM HORARIO) PARA EXIBICAO, SEM SOFRER DESLOCAMENTO DE FUSO HORARIO
  const formatDateOnly = (value) => {
    if (!value) return null;

    const datePart = String(value).slice(0, 10);
    const parsed = new Date(`${datePart}T00:00:00`);

    if (Number.isNaN(parsed.getTime())) return null;

    return parsed.toLocaleDateString();
  };

  // CONVERTE UMA DATA PARA O FORMATO ACEITO PELO INPUT TYPE="DATE" (YYYY-MM-DD)
  const toDateInputValue = (value) => {
    if (!value) return "";

    const parsed = new Date(value);

    if (Number.isNaN(parsed.getTime())) return "";

    return parsed.toISOString().slice(0, 10);
  };

  const formatDateTime = (value) => {
    if (!value) return "Sem informacoes";

    const parsed = new Date(value);

    if (Number.isNaN(parsed.getTime())) {
      return "Sem informacoes";
    }

    return parsed.toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleChangeCompanyOperatingStatus = async () => {
    try {
      if (!company?.id) return;

      setAccountOperatingAlert(false);
      setLoadingInformations(true);

      const res = await axios.post(`/admin/companies/status/update`, {
        company_id: company.id,
        status: !company.operating,
      });

      if (res.status == 200) {
        setWarnAlert({
          show: true,
          title: "Alerta",
          text: `O status de operacao da empresa foi alterado com sucesso!\n\nAgora a empresa ${company.name} ${company.operating ? "nao esta" : "esta"} liberada para atividades`,
        });

        setCompany((previous) => ({
          ...previous,
          operating: !previous.operating,
        }));
      }

    } catch (error) {
      console.error("ERROR - COMPANY STATUS CONTROLLER:", error);
      setWarnAlert({
        show: true,
        title: "Erro",
        text: "Nao foi possivel alterar o status da empresa no momento.",
      });

    } finally {
      setLoadingInformations(false);
    }
  };

  const handleDeleteCompany = async () => {
    try {
      if (!company?.id) return;

      setDeleting(true);

      await deleteCompany({ companyId: company.id });

      setCompanyToDelete(false);
      await handleGetCompanyData();

    } catch (error) {
      console.error("ERROR - DELETE COMPANY FUNCTION:", error);
      setCompanyToDelete(false);
      setWarnAlert({
        show: true,
        title: "Erro",
        text: error?.response?.data?.error || "Nao foi possivel excluir a empresa no momento.",
      });

    } finally {
      setDeleting(false);
    }
  };

  const handleRestoreCompany = async () => {
    try {
      if (!company?.id) return;

      setRestoring(true);

      await restoreCompany({ companyId: company.id });

      await handleGetCompanyData();

      setWarnAlert({
        show: true,
        title: "Alerta",
        text: "Empresa restaurada com sucesso!",
      });

    } catch (error) {
      console.error("ERROR - RESTORE COMPANY FUNCTION:", error);
      setWarnAlert({
        show: true,
        title: "Erro",
        text: "Nao foi possivel restaurar a empresa no momento.",
      });

    } finally {
      setRestoring(false);
    }
  };

  const handleConfirmPayment = async () => {
    try {
      if (!company?.id || !nextPaymentDateValue) return;

      setLoadingInformations(true);
      setConfirmPaymentDateAlert(false);

      const next_payment = nextPaymentDateValue;

      const res = await axios.post(`/admin/companies/payments/register`, {
        companyId: company.id,
        next_payment,
        expected_day: company.payment_control.next_payment,
        confirmed_date: confirmPaymentDateValue
          ? new Date(confirmPaymentDateValue).toISOString()
          : new Date().toISOString(),
      });

      if (res.status == 200) {
        setWarnAlert({
          show: true,
          title: "Alerta",
          text: "O pagamento da mensalidade referente a empresa selecionada foi registrado com sucesso!",
        });

        // O billing_status, o historico de pagamentos e a conversao do trial sao derivados no
        // backend a partir de varias tabelas — recarrega os dados em vez de tentar replicar essa
        // conta no cliente, para nao arriscar exibir um status desatualizado ou incorreto.
        await handleGetCompanyData();
      }

    } catch (error) {
      console.error("ERROR - CONFIRM PAYMENT FUNCTION:", error);
      setWarnAlert({
        show: true,
        title: "Erro",
        text: "Nao foi possivel confirmar o pagamento no momento. Tente novamente em instantes ou entre em contato com a equipe de desenvolvimento para averiguar a situacao!",
      });

    } finally {
      setLoadingInformations(false);
    }
  };

  const handleExtendTrial = async () => {
    try {
      if (!company?.id || !trialData?.id || !extendTrialDateValue) {
        setWarnAlert({
          show: true,
          title: "Erro",
          text: "Não foi possível extender a data de testes da empresa selecionada. Tente novamente mais tarde.",
        });
        return;
      };

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const selectedDate = new Date(`${extendTrialDateValue}T00:00:00`);

      if (selectedDate <= today) {
        setWarnAlert({
          show: true,
          title: "Atencao",
          text: "A nova data de finalizacao do trial deve ser posterior a data atual.",
        });
        return;
      }

      setLoadingInformations(true);
      setExtendTrialAlert(false);

      await axios.post(`/admin/companies/trial/extend`, {
        companyId: company.id,
        finalDate: extendTrialDateValue,
      });

      setWarnAlert({
        show: true,
        title: "Alerta",
        text: "A data de finalizacao do trial foi atualizada com sucesso!",
      });

      // O backend tambem ajusta companies.payments.next_payment ao estender o trial (para o cron
      // de bloqueio por atraso nao reverter a extensao no dia seguinte) — recarrega os dados em vez
      // de tentar replicar esse calculo no cliente.
      await handleGetCompanyData();

    } catch (error) {
      console.error("ERROR - EXTEND TRIAL FUNCTION:", error);
      setWarnAlert({
        show: true,
        title: "Erro",
        text: error?.response?.data?.error || "Nao foi possivel estender o trial no momento.",
      });

    } finally {
      setLoadingInformations(false);
    }
  };

  const handleUpdateNextPayment = async () => {
    try {
      if (!company?.id || !editNextPaymentDateValue || editNextPaymentCost === "") {
        setWarnAlert({
          show: true,
          title: "Atenção",
          text: "Informe um valor e uma data válidos para o próximo pagamento antes de salvar.",
        });
        return;
      }

      const normalizedCost = Number(normalizeValues(editNextPaymentCost));

      if (Number.isNaN(normalizedCost) || normalizedCost < 0) {
        setWarnAlert({
          show: true,
          title: "Atenção",
          text: "Informe um valor de mensalidade válido antes de salvar.",
        });
        return;
      }

      setLoadingInformations(true);
      setEditNextPaymentAlert(false);

      await updateNextPayment({
        companyId: company.id,
        cost: normalizedCost,
        nextPayment: editNextPaymentDateValue,
      });

      setWarnAlert({
        show: true,
        title: "Alerta",
        text: "O próximo pagamento da empresa foi atualizado com sucesso!",
      });

      await handleGetCompanyData();

    } catch (error) {
      console.error("ERROR - UPDATE NEXT PAYMENT FUNCTION:", error);
      setWarnAlert({
        show: true,
        title: "Erro",
        text: error?.response?.data?.error || "Não foi possível atualizar o próximo pagamento no momento.",
      });

    } finally {
      setLoadingInformations(false);
    }
  };

  const handleOpenWhatsapp = () => {
    if (!company?.ownerData?.phone) return;

    const phone = company.ownerData.phone
      .replace("(", "")
      .replace(")", "")
      .replace("-", "")
      .replaceAll(" ", "");

    window.open(`https://wa.me/${phone}`);
  };

  const trialData = company?.trial && company.trial[0] ? company.trial[0] : null;

  return (
    <div>
      <Header>
        <div className={styles.headerContent}>
          <button className={styles.backButton} onClick={() => navigate("/clients")}> 
            <IoIosArrowBack size={18} color="#fff" />
            <span>Voltar</span>
          </button>

          <h2 className={styles.title}>Detalhes do cliente</h2>
        </div>

        <button
          className={styles.editButton}
          disabled={!company || loading || company?.deleted}
          onClick={() => setShowEditCompanyDataComponent(true)}
        >
          <BiSolidEdit size={18} color="#fff" />
          <span>Editar</span>
        </button>
      </Header>

      <div className={styles.pageContainer}>

        {loading || loadingInformations ? (
          <div className={styles.loadingContainer}>
            <Loading />
          </div>

        ) : screenError ? (
          <div className={styles.feedbackCard}>
            <h3>Falha ao carregar cliente</h3>
            <p>{screenError}</p>
            <button className={styles.primaryButton} onClick={() => navigate("/clients")}>
              <IoIosArrowBack size={16} />
              <span>Voltar para lista</span>
            </button>
          </div>

        ) : (
          <div className={styles.contentArea}>
            <section className={styles.heroCard}>
              <div>
                <p className={styles.eyebrow}>Empresa</p>
                <h1 className={styles.companyName}>{company?.name || "Sem nome"}</h1>
                <p className={styles.subtitle}>{company?.razao_social || "Razao social nao informada"}</p>
              </div>

              <div className={styles.statusArea}>
                <span
                  className={styles.statusTag}
                  style={{ backgroundColor: company?.operating ? "var(--color-green)" : "var(--color-red)" }}
                >
                  {company?.operating ? "Ativa" : "Inativa"}
                </span>
                <span className={styles.documentTag}>{company?.document || "Documento nao informado"}</span>
              </div>
            </section>

            {company?.deleted && (
              <div className={styles.deletedBanner}>
                <span>Empresa excluida em {formatDateTime(company.deleted_at)}.</span>
                <button
                  disabled={restoring}
                  onClick={handleRestoreCompany}
                >
                  <FiRotateCcw size={16} />
                  <span>Restaurar empresa</span>
                </button>
              </div>
            )}

            <section className={styles.metricsSection}>
              <article className={styles.infoCard}>
                <h3 className={styles.cardTitle}>Produtos cadastrados</h3>
                <p className={styles.metricValue}>{metrics.productsCount}</p>
                <p><b>Ultimo cadastro:</b> {formatDateTime(metrics.lastProductAt)}</p>
              </article>

              <article className={styles.infoCard}>
                <div className={styles.customersCardHeader}>
                  <h3 className={styles.cardTitle}>Clientes cadastrados</h3>
                  <button
                    className={styles.primaryButton}
                    style={{ width: 150 }}
                    type="button"
                    onClick={() => setCustomersModalOpen(true)}
                  >
                    <ExternalLink size={16} />
                    <span>Ver mais</span>
                  </button>
                </div>

                <p className={styles.metricValue}>{metrics.customersCount}</p>
                <p><b>Ultimo cadastro:</b> {formatDateTime(metrics.lastCustomerAt)}</p>
              </article>

              <article className={styles.infoCard}>
                <div className={styles.loginsCardHeader}>
                  <h3 className={styles.cardTitle}>Logins</h3>
                  <button
                    className={styles.primaryButton}
                    style={{ width: 150 }}
                    type="button"
                    onClick={() => setLoginsModalOpen(true)}
                  >
                    <ExternalLink size={16} />
                    <span>Ver mais</span>
                  </button>
                </div>

                <p className={styles.metricValue}>{metrics.loginsPerDay}</p>
                <p><b>Ultimo login:</b> {formatDateTime(metrics.lastLoginAt)}</p>
              </article>

              <article className={styles.infoCard}>
                <div className={styles.loginsCardHeader}>
                  <h3 className={styles.cardTitle}>Colaboradores</h3>
                  <button
                    className={styles.primaryButton}
                    style={{ width: 150 }}
                    type="button"
                    onClick={() => setCollaboratorsModalOpen(true)}
                  >
                    <ExternalLink size={16} />
                    <span>Ver mais</span>
                  </button>
                </div>

                <p className={styles.metricValue}>{metrics.collaboratorsCount}</p>
              </article>

              <article className={`${styles.infoCard} ${styles.notesCard}`}>
                <div className={styles.notesHeader}>
                  <h3 className={styles.cardTitle}>Anotacoes sobre o cliente</h3>
                  <button
                    className={styles.primaryButton}
                    type="button"
                    onClick={() => setNotesModalOpen(true)}
                  >
                    <FiEdit2 size={16} />
                    <span>Editar anotacoes</span>
                  </button>
                </div>

                <p className={styles.notesPreview}>
                  {(company?.notes || "").trim() ? company.notes : "Sem anotacoes cadastradas."}
                </p>
              </article>
            </section>

            <section className={styles.gridArea}>
              <article className={styles.infoCard}>
                <h3 className={styles.cardTitle}>Dados da empresa</h3>
                <p><b>Regime tributario:</b> {company?.fiscal?.regime_tributario || "Sem dados"}</p>
                <p><b>Razao Social:</b> {company?.razao_social || "Sem dados"}</p>
                <p><b>Nome fantasia:</b> {company?.name || "Sem dados"}</p>
                <p><b>{company?.document_type || "Documento"}:</b> {company?.document || "Sem dados"}</p>
                <p><b>Email:</b> {company?.email || "Sem dados"}</p>
                <p><b>Telefone:</b> {company?.phone || "Sem dados"}</p>
              </article>

              <article className={styles.infoCard}>
                <h3 className={styles.cardTitle}>Dados do proprietario</h3>
                <p><b>Nome:</b> {company?.ownerData?.name || "Sem dados"}</p>
                <p><b>Telefone:</b> {company?.ownerData?.phone || "Sem dados"}</p>
                <p><b>CPF:</b> {company?.ownerData?.document || "Sem dados"}</p>
                <p><b>Email:</b> {company?.ownerData?.email || "Sem dados"}</p>

                <div className={styles.actionRow}>
                  <button className={styles.successButton} onClick={handleOpenWhatsapp}>
                    <FaWhatsapp size={16} />
                    <span>Entrar em contato</span>
                  </button>

                  {loggedAdmin.can_view_users_pass && company?.ownerData?.id ? (
                    <button className={styles.successButton} style={{backgroundColor: '#005586'}} onClick={() => setUserBindingsModalOpen(true)}>
                      {/* <FiKey size={16} /> */}
                      <User size={16}/>
                      <span>Ver usuário</span>
                    </button>
                  ) : null}
                </div>
              </article>

              {trialData ? (
                <article className={styles.infoCard}>
                  <h3 className={styles.cardTitle}>Trial da plataforma</h3>
                  <p><b>Status:</b> {trialData.status == "active" && new Date(trialData.end_date) >= new Date() ? "Ativo" : "Expirado"}</p>
                  <p><b>Data de inicio:</b> {new Date(trialData.start_date + "T00:00:00").toLocaleDateString()}</p>
                  <p><b>Data de finalizacao:</b> {new Date(trialData.end_date + "T00:00:00").toLocaleDateString()}</p>
                  <p><b>Convertido a cliente:</b> {trialData.converted_to_plan == true ? "Sim" : "Nao"}</p>

                  <div className={styles.actionRow}>
                    {/* Empresas que ja converteram para plano pago (ou que ja tem pagamentos
                        confirmados, mesmo que o trial nao reflita isso) nao precisam mais de
                        extensao de trial. */}
                    {!trialData.converted_to_plan && !['ACTIVE', 'OVERDUE'].includes(company?.billing_status) ? (
                      <button
                        className={styles.infoButton}
                        disabled={company?.deleted}
                        onClick={() => {
                          setExtendTrialDateValue(toDateInputValue(trialData.end_date));
                          setExtendTrialAlert(true);
                        }}
                      >
                        <FiCalendar size={16} />
                        <span>Estender trial</span>
                      </button>
                    ) : null}
                  </div>
                </article>

              ) : (
                <article className={styles.infoCard}>
                  <h3 className={styles.cardTitle}>Trial da plataforma</h3>
                  <p>A empresa nao possui dados de trial cadastrados.</p>
                </article>
              )}

              <article className={styles.infoCard}>
                <h3 className={styles.cardTitle}>Dados de pagamento</h3>

                {company?.payment_control?.id ? (
                  <>
                    <p>
                      <b>Status:</b>{" "}
                      <span
                        className={styles.overdueTag}
                        style={{ backgroundColor: (BILLING_STATUS_META[company.billing_status] || BILLING_STATUS_META.NO_PAYMENT).color }}
                      >
                        {(BILLING_STATUS_META[company.billing_status] || BILLING_STATUS_META.NO_PAYMENT).text}
                      </span>
                    </p>
                    <p><b>Mensalidade:</b> {formatValues(company.payment_control.cost || "00.00")}</p>
                    <p><b>Dia de pagamento:</b> {company.payment_control.payday || "Nao registrado"}</p>
                    <p>
                      <b>Proximo pagamento:</b>{" "}
                      {formatDateOnly(company.payment_control.next_payment) || "Nao registrado"}
                      {" "}
                      <button
                        className={styles.inlineEditButton}
                        disabled={company?.deleted}
                        title="Editar proximo pagamento"
                        onClick={() => {
                          setEditNextPaymentCost(formatValues(Number(company.payment_control.cost) || 0));
                          setEditNextPaymentDateValue(toDateInputValue(company.payment_control.next_payment));
                          setEditNextPaymentAlert(true);
                        }}
                      >
                        <FiEdit2 size={14} />
                      </button>
                    </p>
                    <p>
                      <b>Pagamentos confirmados:</b> {company.payments_count ?? 0}
                    </p>
                    <p>
                      <b>Ultimo pagamento:</b> {formatDateOnly(company.last_payment_at) || "Nenhum pagamento registrado"}
                    </p>

                    <div className={styles.actionRow}>
                      <button
                        className={styles.dangerButton}
                        style={{ backgroundColor: company?.operating ? "var(--color-red)" : "var(--color-green)" }}
                        disabled={company?.deleted}
                        onClick={() => setAccountOperatingAlert(true)}
                      >
                        {company?.operating ? <FiLock size={16} /> : <FiUnlock size={16} />}
                        <span>{company?.operating ? "Bloquear empresa" : "Liberar empresa"}</span>
                      </button>

                      <button
                        className={styles.successButton}
                        disabled={company?.deleted}
                        onClick={() => setConfirmPaymentAlert(true)}
                      >
                        <FiCheckCircle size={16} />
                        <span>Confirmar pagamento</span>
                      </button>

                      {!company?.deleted && (
                        <button
                          className={styles.dangerButton}
                          style={{ backgroundColor: "var(--color-red)" }}
                          disabled={company?.operating}
                          title={company?.operating ? "Bloqueie a empresa antes de excluir" : undefined}
                          onClick={() => setCompanyToDelete(true)}
                        >
                          <FiTrash2 size={16} />
                          <span>Excluir empresa</span>
                        </button>
                      )}
                    </div>
                  </>

                ) : (
                  <p>Necessario cadastrar dados de pagamento.</p>
                )}
              </article>

              <article className={styles.infoCard}>
                <h3 className={styles.cardTitle}>Endereco de cobranca</h3>
                <p><b>CEP:</b> {company?.address?.cep || "Sem dados"}</p>
                <p><b>Cidade:</b> {company?.address?.city || "Sem dados"}</p>
                <p><b>UF:</b> {company?.address?.state || "Sem dados"}</p>
                <p><b>Rua:</b> {company?.address?.street || "Sem dados"}</p>
                <p><b>Bairro:</b> {company?.address?.district || "Sem dados"}</p>
                <p><b>Numero:</b> {company?.address?.number || "Sem dados"}</p>
                <p><b>Latitude:</b> {company?.address?.latitude ?? "Sem informacoes"}</p>
                <p><b>Longitude:</b> {company?.address?.longitude ?? "Sem informacoes"}</p>
                <p><b>Raio de atuacao:</b> {company?.address?.radius ?? "Sem informacoes"}</p>

                <div className={styles.actionRow}>
                  <button className={styles.infoButton} onClick={() => setConfirmAddressDataAlert(true)}>
                    <FiMapPin size={16} />
                    <span>Conferir informacoes</span>
                  </button>
                </div>
              </article>

              <article className={styles.infoCard}>
                <h3 className={styles.cardTitle}>Permissoes da empresa</h3>
                <p><b>Emite NFE:</b> {company?.privileges?.use_invoice ? "Sim" : "Nao"}</p>
                <p><b>Acesso ao smartmenu:</b> {company?.privileges?.use_smartmenu ? "Sim" : "Nao"}</p>
                <p><b>Integracao ao Ifood:</b> {company?.privileges?.use_ifood ? "Sim" : "Nao"}</p>
                <p><b>Usa controle de estoque:</b> {company?.privileges?.use_stock ? "Sim" : "Nao"}</p>
              </article>
            </section>
          </div>
        )}
      </div>

      <ConfirmAddressData
        show={confirmAddressDataAlert}
        postalCode={company?.address?.cep}
        close={() => setConfirmAddressDataAlert(false)}
      />

      <EditData
        view={showEditCompanyDataComponent}
        data={company}
        returnAction={() => {
          setShowEditCompanyDataComponent(false);
          handleGetCompanyData();
        }}
        close={() => setShowEditCompanyDataComponent(false)}
      />

      <BoxAlert
        show={accountOperatingAlert}
        title={company?.operating ? "Bloquear" : "Liberar"}
        text={`Tem certeza que voce deseja ${company?.operating ? "bloquear" : "liberar"} a conta da empresa "${company?.name}" para operar no sistema?`}
        showButtons={true}
        cancelButtonText={"Cancelar"}
        confirmButtonText={company?.operating ? "Bloquear" : "Liberar"}
        confirmFunction={() => handleChangeCompanyOperatingStatus()}
        onClose={() => setAccountOperatingAlert(false)}
      />

      <BoxAlert
        show={confirmPaymentAlert}
        title={"Confirmar pagamento"}
        text={`Tem certeza que voce deseja confirmar um pagamento de mensalidade para a empresa "${company?.name}"?`}
        showButtons={true}
        cancelButtonText={"Cancelar"}
        confirmButtonText={"Prosseguir"}
        confirmFunction={() => {
          setConfirmPaymentDateValue(toDateInputValue(new Date()));
          setNextPaymentDateValue(null);
          setConfirmPaymentDateAlert(true);
          setConfirmPaymentAlert(false);
        }}
        onClose={() => setConfirmPaymentAlert(false)}
      />

      <BoxAlert
        show={confirmPaymentDateAlert}
        title={"Confirmar pagamento"}
        text={"Informe a data em que o pagamento foi realizado e a data prevista para o proximo pagamento."}
        input={
          <div className={styles.paymentDatesArea}>
            <div className={styles.inputAreaConfig}>
              <label className={styles.labelInput}>Data do pagamento realizado:</label>
              <input
                className={styles.inputConfig}
                type="date"
                value={confirmPaymentDateValue ?? ""}
                onChange={({ target: { value } }) => setConfirmPaymentDateValue(value)}
              />
            </div>

            <div className={styles.inputAreaConfig} style={{ marginTop: 15 }}>
              <label className={styles.labelInput}>Proximo pagamento previsto:</label>
              <input
                className={styles.inputConfig}
                type="date"
                value={nextPaymentDateValue ?? ""}
                onChange={({ target: { value } }) => setNextPaymentDateValue(value)}
              />
            </div>
          </div>
        }
        showButtons={true}
        cancelButtonText={"Cancelar"}
        confirmButtonText={"Confirmar"}
        confirmFunction={() => {
          if (!nextPaymentDateValue) {
            setWarnAlert({
              show: true,
              title: "Atencao",
              text: "Informe a data prevista para o proximo pagamento antes de confirmar.",
            });
            return;
          }

          handleConfirmPayment();
        }}
        onClose={() => setConfirmPaymentDateAlert(false)}
      />

      <BoxAlert
        show={extendTrialAlert}
        title={"Estender trial"}
        text={"Informe a nova data de finalizacao do periodo de teste da empresa."}
        input={
          <div className={styles.inputAreaConfig}>
            <label className={styles.labelInput}>Nova data de finalizacao:</label>
            <input
              className={styles.inputConfig}
              type="date"
              value={extendTrialDateValue ?? ""}
              onChange={({ target: { value } }) => setExtendTrialDateValue(value)}
            />
          </div>
        }
        showButtons={true}
        cancelButtonText={"Cancelar"}
        confirmButtonText={"Salvar"}
        confirmFunction={() => {
          if (!extendTrialDateValue) {
            setWarnAlert({
              show: true,
              title: "Atencao",
              text: "Informe a nova data de finalizacao do trial antes de salvar.",
            });
            return;
          }

          handleExtendTrial();
        }}
        onClose={() => setExtendTrialAlert(false)}
      />

      <BoxAlert
        show={editNextPaymentAlert}
        title={"Editar próximo pagamento"}
        text={"Ajuste o valor da mensalidade e/ou a data prevista para o próximo pagamento da empresa."}
        input={
          <div className={styles.paymentDatesArea}>
            <div className={styles.inputAreaConfig}>
              <label className={styles.labelInput}>Valor da mensalidade:</label>
              <input
                className={styles.inputConfig}
                type="text"
                placeholder="R$ 00,00"
                value={editNextPaymentCost}
                onChange={({ target: { value } }) => formatInputCurrencyValues({ value, saveAction: setEditNextPaymentCost })}
              />
            </div>

            <div className={styles.inputAreaConfig} style={{ marginTop: 15 }}>
              <label className={styles.labelInput}>Próximo pagamento previsto:</label>
              <input
                className={styles.inputConfig}
                type="date"
                value={editNextPaymentDateValue ?? ""}
                onChange={({ target: { value } }) => setEditNextPaymentDateValue(value)}
              />
            </div>
          </div>
        }
        showButtons={true}
        cancelButtonText={"Cancelar"}
        confirmButtonText={"Salvar"}
        confirmFunction={handleUpdateNextPayment}
        onClose={() => setEditNextPaymentAlert(false)}
      />

      <BoxAlert
        show={companyToDelete && !deleting}
        title={"Excluir empresa"}
        text={`Tem certeza que voce deseja excluir a empresa "${company?.name}" (${company?.razao_social || "razao social nao informada"}, ${company?.document || "documento nao informado"})? Essa acao pode ser revertida a qualquer momento pelo botao "Restaurar empresa".`}
        showButtons={true}
        cancelButtonText={"Cancelar"}
        confirmButtonText={"Excluir"}
        confirmFunction={handleDeleteCompany}
        onClose={() => setCompanyToDelete(false)}
      />

      <ClientNoteModal
        show={notesModalOpen}
        companyId={company?.id}
        initialNotes={company?.notes || ""}
        onClose={() => {
          setNotesModalOpen(false);
        }}
        onSaved={(savedNotes) => {
          setCompany((previous) => ({
            ...previous,
            notes: savedNotes,
          }));

          setWarnAlert({
            show: true,
            title: "Alerta",
            text: "As anotacoes da empresa foram atualizadas com sucesso!",
          });
        }}
        onError={(error) => {
          console.error("ERROR - SAVE COMPANY NOTES FUNCTION:", error);

          setWarnAlert({
            show: true,
            title: "Erro",
            text: error?.response?.data?.error || "Nao foi possivel salvar as anotacoes no momento.",
          });
        }}
      />

      <ClientCustomersModal
        show={customersModalOpen}
        companyId={company?.id}
        formatDateTime={formatDateTime}
        onClose={() => {
          setCustomersModalOpen(false);
        }}
      />

      <ClientLoginsModal
        show={loginsModalOpen}
        companyId={company?.id}
        formatDateTime={formatDateTime}
        onClose={() => {
          setLoginsModalOpen(false);
        }}
      />

      <ClientCollaboratorsModal
        show={collaboratorsModalOpen}
        companyId={company?.id}
        onClose={() => {
          setCollaboratorsModalOpen(false);
        }}
      />

      <UserBindingsModal
        show={userBindingsModalOpen}
        user={company?.ownerData}
        canViewAccess={loggedAdmin.can_view_users_pass}
        canAccessAccount={loggedAdmin.can_view_users_pass} /* QUEM VÊ A SENHA DO USUÁRIO TAMBÉM PODE ACESSAR A CONTA (SESSÃO DE SUPORTE AUDITADA) */
        onClose={() => {
          setUserBindingsModalOpen(false);
        }}
      />

      <BoxAlert
        show={warnAlert.show}
        title={warnAlert.title}
        text={warnAlert.text}
        onClose={() => setWarnAlert({ show: false, title: null, text: null })}
      />
    </div>
  );
}
